<script setup lang="ts">
import { useGameState } from '@/stores/gameState'
import type { Combination, ActionData } from '@acepoker/api-core'
import { Tabs } from 'radix-vue/namespaced'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import PlayingCard from './table/PlayingCard.vue'
import type { StrategyData } from '@acepoker/api-core'
import type { Cell } from '@acepoker/api-core'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import Scroll from './Scroll.vue'

const { t } = useI18n()

const { getHoleCardsForPlayer } = useGameState()
const { player } = storeToRefs(useGameState())

const rankMap = {
  '2': 0,
  '3': 1,
  '4': 2,
  '5': 3,
  '6': 4,
  '7': 5,
  '8': 6,
  '9': 7,
  T: 8,
  J: 9,
  Q: 10,
  K: 11,
  A: 12
}

const convertCombinationToGroup = (combination: string) => {
  const [aRank, aSuit, bRank, bSuit] = combination.split('')
  const aRankInt = rankMap[aRank as keyof typeof rankMap]
  const bRankInt = rankMap[bRank as keyof typeof rankMap]
  let name = aRankInt > bRankInt ? aRank! + bRank! : bRank! + aRank!
  if (aSuit === bSuit) {
    name += 's'
  }
  if (aSuit !== bSuit && aRank !== bRank) {
    name += 'o'
  }
  return name
}

const props = defineProps<{
  strategyData: StrategyData
}>()

const currentCellName = ref<string>('')
const currentCell = computed<Cell | null>(() => {
  const { getHoleCardsForPlayer, player } = useGameState()
  const playerCards = getHoleCardsForPlayer(player?.playerId || '')
  const cellName =
    currentCellName.value ||
    (playerCards ? convertCombinationToGroup(playerCards?.slice().reverse().join('')) : '')
  for (let i = 0; i < props.strategyData.matrix.length; i++) {
    const row = props.strategyData.matrix[i]
    for (let j = 0; j < row.length; j++) {
      const col = row[j]
      if (col.name === cellName) {
        return col
      }
    }
  }
  return null
})
const currentCellEVGroup = computed<[Combination, ActionData][]>(() => {
  if (currentCell.value === null) return []
  return Object.entries(props.strategyData.groups[currentCell.value.name])
})

const scrollToEvCombination = (combination: string) => {
  const element = document.getElementById(combination)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  } else {
    console.warn('No element found')
  }
}

const currentEvCombination = ref<Combination>(
  (() => {
    if (player.value?.playerId) {
      return (
        getHoleCardsForPlayer(player.value.playerId)
          ?.sort((a = '', b = '') => {
            const aRank = rankMap[a!.split('')[0] as keyof typeof rankMap] ?? 0
            const bRank = rankMap[b!.split('')[0] as keyof typeof rankMap] ?? 0
            return bRank - aRank
          })
          ?.join('') ?? ''
      )
    }
    if (currentCellEVGroup.value.length !== 0) {
      return currentCellEVGroup.value[0][0]
    }
    return ''
  })()
)

onMounted(() => {
  scrollToEvCombination(currentEvCombination.value)
})

const grid = computed(() => props.strategyData.matrix.flat())

const labelMap = {
  raise: 'Raise',
  bet: 'Bet',
  fold: 'Fold',
  allin: 'AllIn',
  check: 'Check',
  call: 'Call'
}

const calcCellFillData = (cell: Cell) => {
  const actionEntries = (Object.entries(cell.actions) as [string, number][]).sort((_, [b]) => {
    if (b === 'f') {
      return -1
    }
    if (b.startsWith('allin')) {
      return -1
    }
    return 0
  })
  const result: {
    actionclass: string
    percent: number | string
  }[] = []
  let bIndex = 1
  for (const [action, strategy] of actionEntries) {
    const actionclass =
      action[0] === 'r' || action[0] === 'b' ? action[0] + bIndex++ : action.replace(/[0-9]/g, '')

    result.push({
      actionclass,
      percent: strategy < 0.01 ? 0 : (strategy * 100).toFixed(1)
    })
  }
  return result
}

const calcActionsData = (actions: ActionData) => {
  const actionEntries = Object.entries(actions).sort((_, [b]) => {
    if (b === 'x') {
      return -1
    }
    if (b.startsWith('allin')) {
      return -1
    }
    return 0
  })
  const result: {
    action: string
    actionclass: string
    percent: number | string
    label: string
    suffix?: string
    amount?: number | null
    ev: string
  }[] = []
  let bIndex = 1
  for (const [action_name, { action, strategy, ev, amount }] of actionEntries) {
    const MAX_ACTIONS_COLOR_LEVEL = 5
    const actionclass =
      action[0] === 'r' || action[0] === 'b'
        ? action[0] + Math.min(bIndex++, MAX_ACTIONS_COLOR_LEVEL)
        : action_name.replace(/[0-9]/g, '')
    const suffix = action[0] === 'r' || action[0] === 'b' ? ` ${action_name.slice(1)}%` : ''
    result.push({
      action,
      actionclass,
      percent: strategy < 0.01 ? 0 : (strategy * 100).toFixed(1),
      label: labelMap[action as keyof typeof labelMap],
      suffix,
      amount,
      ev: ev.toFixed(2)
    })
  }
  return result
}

const onCellClick = (cellName: string) => {
  currentCellName.value = cellName
  currentEvCombination.value = currentCellEVGroup.value[0][0]
}

onUnmounted(() => {
  currentCellName.value = ''
})
</script>

<template>
  <div class="strategy">
    <div class="strategy-box" v-if="grid.length !== 0">
      <div
        :class="{
          'strategy-cell': true,
          'strategy-cell__active': currentCell?.name === cell.name
        }"
        v-for="(cell, i) of grid"
        :key="i"
        @click="onCellClick(cell.name)"
      >
        {{ cell.name }}
        <div class="strategy-cell-bg" />
        <div class="strategy-cell-actions">
          <div
            :key="j"
            :class="`strategy-cell-action strategy-cell-action-${actionclass}`"
            v-for="({ actionclass, percent }, j) of calcCellFillData(cell)"
            :style="{
              width: percent + '%'
            }"
          />
        </div>
      </div>
    </div>
    <div class="strategy-current" v-if="grid.length !== 0">
      <Tabs.Root class="TabsRoot" v-model="currentEvCombination">
        <Tabs.List class="TabsList" aria-label="Manage your account">
          <div class="strategy-card-scroll-wrapper">
            <Scroll>
              <div class="strategy-trigger-box">
                <Tabs.Trigger
                  v-for="[combination] of currentCellEVGroup"
                  :key="combination"
                  :value="combination"
                >
                  <div
                    :id="combination"
                    :class="{
                      'strategy-card-wrapper': true,
                      'strategy-card-wrapper__active': combination === currentEvCombination
                    }"
                  >
                    <playing-card :value="combination.slice(0, 2)" class="strategy-card" />
                    <playing-card :value="combination.slice(2)" class="strategy-card" />
                  </div>
                </Tabs.Trigger>
                <div
                  :class="{
                    'strategy-card-wrapper': true
                  }"
                ></div>
              </div>
            </Scroll>
          </div>
        </Tabs.List>
        <Tabs.Content
          v-for="[combination, actions] of currentCellEVGroup"
          :key="combination"
          :value="combination"
          class="TabsContent"
        >
          <div class="strategy-current-hand">
            <div class="strategy-current-hand-title">{{ $t('Strategy.label.Actions') }}</div>
            <div
              :key="j"
              :class="`strategy-current-hand-item`"
              v-for="({ percent, label, ev, actionclass, suffix }, j) of calcActionsData(actions)"
            >
              <div :class="`strategy-cell-action-text-${actionclass} strategy-current-hand-text`">
                {{ t(`label.${label}`) }} {{ suffix }}
              </div>
              <div class="strategy-current-hand-gap" />
              <div class="strategy-current-hand-ev-wrapper">
                <div class="strategy-current-hand-ev-label">{{ $t('Strategy.label.EV') }}:</div>
                <div class="strategy-current-hand-ev-value">{{ ev }}</div>
              </div>
              <div class="strategy-current-hand-percent-wrapper">
                <div :class="`strategy-cell-action-${actionclass} strategy-current-hand-percent`">
                  {{ percent }}%
                </div>
              </div>
            </div>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  </div>
</template>

<style lang="scss">
.strategy {
  width: 100%;

  &-loader {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #b5b4ba;
  }

  &-box {
    display: grid;
    grid-template-columns: repeat(13, 1fr);
    gap: 2px;
    border-radius: 8px;
  }

  &-row {
    display: flex;
    gap: 2px;
  }

  &-cell {
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
    padding: 1px;
    border-radius: 2px;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &__active {
      outline: 2px solid #fff500;
    }

    &:nth-of-type(1) {
      border-top-left-radius: 10px;
    }

    &:nth-of-type(13) {
      border-top-right-radius: 10px;
    }

    &:nth-of-type(157) {
      border-bottom-left-radius: 10px;
    }

    &:nth-of-type(169) {
      border-bottom-right-radius: 10px;
    }

    &-bg {
      background: #41404a;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -2;
    }

    &-actions {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      z-index: -1;
    }

    &-action {
      height: 100%;

      &-r1,
      &-b1 {
        background: #43a3e9;
      }

      &-r2,
      &-b2 {
        background: #3b95db;
      }

      &-r3,
      &-b3 {
        background: #3387cd;
      }

      &-r4,
      &-b4 {
        background: #2b79bf;
      }

      &-r5,
      &-b5 {
        background: #236bb1;
      }

      &-allin {
        background: #1d5ba1;
      }

      &-f {
        background: #a93939;
      }

      &-c,
      &-x {
        background: #279a33;
      }

      &-text {
        &-r1,
        &-b1 {
          color: #43a3e9;
        }

        &-r2,
        &-b2 {
          color: #3b95db;
        }

        &-r3,
        &-b3 {
          color: #3387cd;
        }

        &-r4,
        &-b4 {
          color: #2b79bf;
        }

        &-r5,
        &-b5 {
          color: #236bb1;
        }

        &-allin {
          color: #1d5ba1;
        }

        &-f {
          color: #a93939;
        }

        &-c,
        &-x {
          color: #279a33;
        }
      }
    }
  }

  &-card-scroll-wrapper {
    overflow-x: auto;
    max-width: 326px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &-trigger-box {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
  }

  &-card-wrapper {
    display: flex;
    flex-wrap: nowrap;
    padding: 6px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    margin: 0px 6px;

    &__active {
      background: rgba(255, 255, 255, 0.6);
    }
  }

  &-card {
    --card-width: 30px;
    --card-height: 40px;
    background-color: white;

    &:last-of-type {
      margin-left: -8px;
    }
  }

  &-current {
    margin-top: 6px;

    &-hand {
      border-top: 4px solid rgba(255, 255, 255, 0.6);
      display: flex;
      flex-direction: column;
      gap: 4px;
      background: #17171b;
      padding: 8px;
      border-radius: 8px;

      &-title {
        font-size: 16px;
        line-height: 24px;
        font-weight: 500;
        margin-bottom: 4px;
        color: white;
      }

      &-item {
        display: flex;
        align-items: center;
        height: 30px;
      }

      &-ev-wrapper {
        display: flex;
        width: 50px;
        height: 100%;
        gap: 4px;
        margin-right: 8px;
        align-items: center;
      }

      &-ev-label {
        font-size: 12px;
        line-height: 12px;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.6);
      }

      &-ev-value {
        font-size: 12px;
        line-height: 52px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.8);
      }

      &-text {
        font-size: 14px;
        line-height: 21px;
        font-weight: 600;
      }

      &-gap {
        flex-grow: 1;
      }

      &-percent-wrapper {
        width: 60px;
        display: flex;
        justify-content: flex-end;
      }

      &-percent {
        font-size: 14px;
        line-height: 21px;
        font-weight: 600;
        color: white;
        padding: 4px 8px;
        border-radius: 8px;
      }
    }
  }
}

</style>
